/* global navigator DOMParser */
import { batchGroupBy } from "./batchGroupBy";
import { nonSpacerCoords, spacerDistribution } from "./grid";
import { insert, resize } from "../state/structureSlice";
import { v4 as uuidv4 } from "uuid";
import { inheritAboveRowStyle, inheritLeftColumnStyle } from "./stylesInBatch";
import { onChange as onBorderChange } from "../state/bordersSlice";
import { onChange as onSpacerChange, updateSpacerColor } from "../state/spacerStylesSlice";
import { reset } from "../state/selectionSlice";

export const ContentType = {
  Table: "table",
  Text: "text",
  Gridd: "gridd",
};

export const tryParseClipboard = async () => {
  const textContent = await navigator.clipboard.readText();
  const content = await navigator.clipboard.read();
  const contentItems = [];
  for (const item of content) {
    const itemTypes = item.types;
    const typedContent = {};
    for (const type of itemTypes) {
      const blob = await item.getType(type);
      if (blob) {
        const itemContent = await blob.text();
        typedContent[type] = itemContent;
      }
    }
    contentItems.push({ itemTypes, typedContent });
  }

  // Priority detection:
  const griddJson = tryParseGriddJSON(contentItems[0].typedContent["text/plain"]);

  if (griddJson) return { type: ContentType.Gridd, content: griddJson };

  const markdownTable = tryParseSVTable(contentItems[0].typedContent["text/plain"], "|");

  if (markdownTable) return { type: ContentType.Table, content: markdownTable };

  const table = tryParseTable(contentItems[0].typedContent["text/html"]);

  if (table) return { type: ContentType.Table, content: table };

  const htmlInTextTable = tryParseTable(contentItems[0].typedContent["text/plain"]);

  if (htmlInTextTable) return { type: ContentType.Table, content: htmlInTextTable };

  const tabTable = tryParseSVTable(contentItems[0].typedContent["text/plain"], "\t");

  if (tabTable) return { type: ContentType.Table, content: tabTable };

  const csvTable = tryParseSVTable(contentItems[0].typedContent["text/plain"], ",");

  if (csvTable) return { type: ContentType.Table, content: csvTable };

  return { type: ContentType.Text, content: textContent };
};

const tryParseGriddJSON = (text) => {
  try {
    const data = JSON.parse(text);
    if (data && data.selection && data.cells && data.cellStyles) {
      return data;
    }
    return null;
  } catch (err) {
    return null;
  }
};

const tryParseTable = (htmlText) => {
  const parser = new DOMParser();
  const htmlFragment = parser.parseFromString(htmlText, "text/html");
  if (htmlFragment.getElementsByTagName("tbody").length > 0) {
    const parsedTable = [];
    const rows = htmlFragment.getElementsByTagName("tbody")[0].getElementsByTagName("tr");
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const parsedRow = [];
      const columns = row.getElementsByTagName("td");
      for (let j = 0; j < columns.length; j++) {
        const col = columns[j];
        const text = col.innerText;
        parsedRow.push(text);
      }
      parsedTable.push(parsedRow);
    }
    return parsedTable;
  }

  return null;
};

const tryParseSVTable = (text, delimiter) => {
  const lines = text?.split(/\r?\n/)?.filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    return null;
  }

  let skipHeaderSeparator = false;
  if (delimiter === "|" && lines.length > 1) {
    const secondLine = lines[1].trim();
    if (/^[|\-\s:]+$/.test(secondLine)) {
      skipHeaderSeparator = true;
    }
  }

  const hasDelimiterInFirstLine = lines[0].includes(delimiter);
  if (!hasDelimiterInFirstLine) {
    return null;
  }

  const parsedTable = [];
  let lineIndex = 0;

  for (const line of lines) {
    if (skipHeaderSeparator && lineIndex === 1) {
      lineIndex++;
      continue;
    }

    const columns = parseSVLine(line, delimiter);
    if (columns.some((col) => col.trim().length > 0)) {
      parsedTable.push(columns);
    }
    lineIndex++;
  }

  return parsedTable.length > 0 ? parsedTable : null;
};

const parseSVLine = (line, delimiter) => {
  if (delimiter === "|") {
    line = line.trim();
    if (line.startsWith("|")) {
      line = line.substring(1);
    }
    if (line.endsWith("|")) {
      line = line.substring(0, line.length - 1);
    }
  }

  const result = [];
  let current = "";
  let inQuotes = false;
  let quoteChar = null;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (!inQuotes) {
      if (char === '"' || char === "'") {
        inQuotes = true;
        quoteChar = char;
      } else if (char === delimiter && (delimiter !== "," || i + 1 >= line.length || line[i + 1] !== " ")) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    } else {
      if (char === quoteChar) {
        if (i + 1 < line.length && line[i + 1] === quoteChar) {
          current += quoteChar;
          i++;
        } else {
          inQuotes = false;
          quoteChar = null;
        }
      } else {
        current += char;
      }
    }
  }

  result.push(current.trim());

  return result;
};

export const updateOnBox = (dispatch, updateCell, state, table, selectedCell) => {
  if (selectedCell) {
    const coords = nonSpacerCoords(state.gridd.present.structure);
    batchGroupBy.start();
    const availableRows = coords.length - selectedCell.x;
    for (let i = 0; i < Math.min(table.length, availableRows); i++) {
      const row = table[i];
      const availableCols = coords[i].length - selectedCell.y;
      for (let j = 0; j < Math.min(row.length, availableCols); j++) {
        const col = row[j];
        const processedText = col
          .replace(/\\n/g, "\n")
          .replace(/\\t/g, "\t")
          .replace(/\n/g, "<br />")
          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
        const payload = {
          x: coords[i + selectedCell.x][j].x,
          y: coords[i][j + selectedCell.y].y,
          text: processedText,
        };
        //console.log({ payload, selectedCell, availableRows, availableCols });
        dispatch(updateCell(payload));
      }
    }
    batchGroupBy.end();
  }
};

export const updateGridd = async (dispatch, getState, table) => {
  const state = getState();
  const selectedCell =
    state.selection.cells[0] ??
    (state.selection.table ? { x: 0, y: 0 } : { x: state.selection.rows[0] ?? 0, y: state.selection.columns[0] ?? 0 });

  if (selectedCell) {
    const lastCell =
      state.gridd.present.structure.cells[state.gridd.present.structure.rows.length - 1][
        state.gridd.present.structure.columns.length - 1
      ];
    const lastCellStyle = state.gridd.present.cellStyles[lastCell.id];
    let coords = nonSpacerCoords(state.gridd.present.structure);
    const spacers = spacerDistribution(state.gridd.present.structure);
    const spacerHColor = state.gridd.present.spacerStyles.columnColors[spacers.col] || "transparent";
    const spacerVColor = state.gridd.present.spacerStyles.rowColors[spacers.row] || "transparent";
    //console.log("Spacers", { spacers, spacerHColor, spacerVColor });

    const availableRows = coords.length - selectedCell.x;
    const rowsNeeded = Math.max(0, table.length - availableRows);

    const maxRowLength = Math.max(...table.map((row) => row.length));
    const availableCols = coords[0] ? coords[0].length - selectedCell.y : 0;
    const colsNeeded = Math.max(0, maxRowLength - availableCols);

    const nonSpacerRowIndices = coords.map((c) => c[0].x);
    const nonSpacerColumnIndices = coords[0].map(({ y }) => y);
    //console.log("Non spacer row indices", nonSpacerRowIndices);
    //console.log("Non spacer column indices", nonSpacerColumnIndices);

    batchGroupBy.start();
    const rowsLength = state.gridd.present.structure.rows.length;
    if (rowsNeeded > 0) {
      for (let i = 0; i < rowsNeeded; i++) {
        const newCellIds = state.gridd.present.structure.columns.map(() => uuidv4());
        dispatch(
          insert({
            newCellIds,
            rowIndex: rowsLength,
            columnIndex: undefined,
          })
        );

        const sourceRowIndex = nonSpacerRowIndices[nonSpacerRowIndices.length - ((i + 1) % 2 === 0 ? 2 : 1)];

        dispatch(
          resize({
            rowIndex: rowsLength,
            weight: state.gridd.present.structure.rows[sourceRowIndex],
          })
        );

        inheritAboveRowStyle(
          dispatch,
          rowsLength,
          state.gridd.present.structure.cells,
          newCellIds,
          rowsLength - sourceRowIndex
        );

        dispatch(
          onBorderChange({
            columnIndexAdded: undefined,
            rowIndexAdded: rowsLength - 1,
          })
        );
        dispatch(
          onSpacerChange({
            columnIndexAdded: undefined,
            rowIndexAdded: rowsLength - 1,
          })
        );

        if (spacers.vertical > 0 && (i + 1) % spacers.vertical === 0) {
          const newCellIds = state.gridd.present.structure.columns.map(() => uuidv4());
          dispatch(
            insert({
              spacer: true,
              rowIndex: rowsLength,
              newCellIds,
              columnIndex: undefined,
            })
          );

          dispatch(
            onBorderChange({
              columnIndexAdded: undefined,
              rowIndexAdded: rowsLength - 1,
            })
          );
          dispatch(
            onSpacerChange({
              columnIndexAdded: undefined,
              rowIndexAdded: rowsLength - 1,
            })
          );

          dispatch(
            updateSpacerColor({
              type: "row",
              index: rowsLength,
              color: spacerVColor,
            })
          );
        }
      }
    }

    if (colsNeeded > 0) {
      const newState = getState();
      for (let j = 0; j < colsNeeded; j++) {
        //const newCellIds = state.gridd.present.structure.cells.map(() => uuidv4());
        const newCellIds = [];
        const columnIndex = nonSpacerColumnIndices[nonSpacerColumnIndices.length - 1] + 1;
        for (let k = 0; k < rowsLength + rowsNeeded; k++) {
          newCellIds.push(uuidv4());
        }
        dispatch(
          insert({
            newCellIds,
            columnIndex,
            rowIndex: undefined,
          })
        );

        const sourceColumnIndex = nonSpacerColumnIndices[nonSpacerColumnIndices.length - ((j + 1) % 2 === 0 ? 2 : 1)];

        dispatch(
          resize({
            columnIndex,
            weight: state.gridd.present.structure.columns[sourceColumnIndex],
          })
        );

        inheritLeftColumnStyle(
          dispatch,
          columnIndex,
          newState.gridd.present.structure.cells,
          newCellIds,
          lastCellStyle,
          columnIndex - sourceColumnIndex
        );

        dispatch(
          onBorderChange({
            columnIndexAdded: columnIndex - 1 || 0,
            rowIndexAdded: undefined,
          })
        );
        dispatch(
          onSpacerChange({
            columnIndexAdded: columnIndex - 1 || 0,
            rowIndexAdded: undefined,
          })
        );

        if (spacers.horizontal > 0 && (j + 1) % spacers.horizontal === 0) {
          const newCellIds = [];
          const columnIndex = state.gridd.present.structure.columns.length || 0;
          for (let k = 0; k < rowsLength + rowsNeeded; k++) {
            newCellIds.push(uuidv4());
          }
          dispatch(
            insert({
              spacer: true,
              force: true,
              columnIndex: columnIndex,
              newCellIds,
              rowIndex: undefined,
            })
          );

          dispatch(
            onBorderChange({
              columnIndexAdded: columnIndex - 1 || 0,
              rowIndexAdded: undefined,
            })
          );
          dispatch(
            onSpacerChange({
              columnIndexAdded: columnIndex - 1 || 0,
              rowIndexAdded: undefined,
            })
          );

          dispatch(
            updateSpacerColor({
              type: "column",
              index: columnIndex,
              color: spacerHColor,
            })
          );
        }
      }
    }

    batchGroupBy.end();
    dispatch(reset());
  }
};
